/**
 * What a dying monster does. Pure, Babylon-free, tested.
 *
 * ── Why a corpse exists at all ──────────────────────────────────────────────
 *
 * A monster used to *vanish* on the frame it died: the server drops a dead mob from the
 * snapshot, and "absent from the snapshot" is the client's one rule for disposing a mesh.
 * So the thing you had been fighting stopped existing between one frame and the next, and
 * a kill — the whole point of the loop — was the least legible event in it. Asked for
 * directly: *we need a death animation of enemies falling to the ground then maybe fade
 * out*.
 *
 * ── It is decoration over a settled outcome ─────────────────────────────────
 *
 * The same property a projectile has, and the same freedom follows from it: by the time
 * `EVENT_ENTITY_DIED` arrives the mob is already gone server-side — no loot decision, no
 * experience, no collision depends on the body still being there. So a corpse is a mesh
 * the client keeps for a moment after the entity it belonged to has ceased to exist, and
 * it can be dropped at any point without affecting anything.
 *
 * That is why a corpse is **detached** rather than kept in `tracked`: it has no snapshot,
 * no interpolation, no nameplate and no pick target, and leaving it in the live map would
 * mean every pass over that map having to ask "is this one real". It also cannot be
 * targeted, which matters — clicking a corpse and getting *out of range* forever would be
 * a worse bug than the vanishing it replaced.
 *
 * ── The two stages, and why they overlap ────────────────────────────────────
 *
 * **Fall, then fade**, and the fade begins *before* the fall finishes. A body that lands,
 * pauses and then dissolves reads as two separate events; one that starts going out as it
 * comes down reads as one. `FADE_START` is where the second begins, as a fraction of the
 * whole.
 *
 * The numbers are chosen rather than measured, and they say so: nothing in the design
 * documents gives a death duration. They are set against the two things a player is doing
 * at that moment — reading the damage number that killed it, and looking for what dropped
 * — so the body has to be gone before the loot label matters, and present long enough that
 * the kill registers.
 */

/** How long a body takes to go over, in seconds. */
export const FALL_SECONDS = 0.4;

/** How long the whole thing lasts, fall and fade together. */
export const CORPSE_SECONDS = 0.95;

/**
 * When the fade starts, as a fraction of `CORPSE_SECONDS`.
 *
 * Inside the fall, deliberately — see the note above. At 0.3 of 0.95s the body is about
 * three quarters of the way down when it begins to go.
 */
export const FADE_START = 0.3;

/**
 * How far a body tips, in radians. A quarter turn: face down on the floor.
 *
 * Not more: past 90° a body would sink through the floor it is lying on, and the floor is
 * the one surface every zone has.
 */
export const FALL_RADIANS = Math.PI / 2;

/**
 * How many corpses may lie about at once.
 *
 * A cap for the same reason `MAX_BOLTS` is one: a pack wipe is the moment the frame budget
 * is tightest, and it is exactly when every monster dies at once. The oldest goes first, so
 * a burst drops the body that is nearly faded rather than the one that just fell.
 */
export const MAX_CORPSES = 12;

/**
 * Eased progress through the fall, 0 → 1.
 *
 * **Ease-out**: a body goes over fastest at the start and settles rather than slamming.
 * `1 - (1 - t)²` rather than a linear ramp, because linear reads as a hinge rather than as
 * something falling.
 */
export function fallProgress(elapsed: number): number {
  const t = clamp01(elapsed / FALL_SECONDS);
  const remaining = 1 - t;
  return 1 - remaining * remaining;
}

/** How far over the body is, in radians, at `elapsed` seconds. */
export function fallAngle(elapsed: number): number {
  return FALL_RADIANS * fallProgress(elapsed);
}

/**
 * How visible the body is, 1 → 0.
 *
 * Fully opaque until `FADE_START` of the way through, then linear to nothing. Linear here
 * and eased above on purpose: an eased fade has a long invisible tail, during which the
 * mesh is still being drawn and still costing a draw call for nothing anybody can see.
 */
export function corpseAlpha(elapsed: number): number {
  const t = clamp01(elapsed / CORPSE_SECONDS);
  if (t <= FADE_START) return 1;
  return clamp01((1 - t) / (1 - FADE_START));
}

/** Whether the body is done and its meshes can be released. */
export function corpseFinished(elapsed: number): boolean {
  return elapsed >= CORPSE_SECONDS;
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
